import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { SERVICES } from "../data/services";
import { KineticText } from "../components/KineticText";
import { ParticleField } from "../components/ParticleField";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import NotFound from "./NotFound";

const CHAINS = [
  { name: "Ethereum", logo: "https://cdn.simpleicons.org/ethereum/627EEA" },
  { name: "Polygon", logo: "https://cdn.simpleicons.org/polygon/7B3FE4" },
  { name: "BNB Chain", logo: "https://cdn.simpleicons.org/binance/F0B90B" },
  { name: "Solana", logo: "https://cdn.simpleicons.org/solana/9945FF" },
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <NotFound />;

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const prev = SERVICES[(idx - 1 + SERVICES.length) % SERVICES.length];
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const Icon = service.icon;
  const headlineLines = service.headline.split(" ").reduce((acc, word) => {
    const last = acc[acc.length - 1];
    if (last && (last + " " + word).length <= 16) acc[acc.length - 1] = last + " " + word;
    else acc.push(word);
    return acc;
  }, []);

  return (
    <main data-testid={`service-page-${service.slug}`}>
      {/* HERO */}
      <section className="grain relative overflow-hidden bg-night pb-20 pt-36 lg:pb-28 lg:pt-44">
        <ParticleField className="absolute inset-0 h-full w-full" />
        <div className="pointer-events-none absolute -right-32 top-10 h-96 w-96 rounded-full bg-ember/15 blur-[120px]" />
        <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-spark/15 blur-[110px]" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="text-center lg:col-span-7 lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="flex items-center justify-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-spark lg:justify-start"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/5">
                <Icon className="h-4 w-4 text-ember" />
              </span>
              Service {service.num} · {service.name}
            </motion.div>
            <h1 className="mt-7 font-syne text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="service-headline">
              <KineticText lines={headlineLines} delay={0.25} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8 }}
              className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg lg:mx-0"
            >
              {service.excerpt}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-spark/30 to-ember/30 blur-xl" />
              <img
                src={service.image}
                alt={service.name}
                data-testid="service-hero-image"
                className="relative aspect-[4/3] w-full rounded-[1.75rem] border border-white/15 object-cover shadow-2xl"
              />
              <span className="absolute -bottom-5 -left-3 rounded-2xl border border-white/15 bg-night-2/90 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-spark backdrop-blur">
                {service.tagline}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OVERVIEW + DELIVERABLES */}
      <section className="bg-silk py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Overview</p>
            <h2 className="mt-3 font-syne text-2xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Why teams choose SparkGrid for {service.name.toLowerCase()}
            </h2>
            <div className="mt-6 space-y-4 text-sm leading-relaxed text-slate-600 sm:text-base">
              {service.about.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <div className="mt-8">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Stack We Reach For</p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {service.stack.map((t) => (
                  <span key={t} className="rounded-full border border-ink/15 bg-white px-4 py-1.5 font-mono text-xs font-semibold text-ink transition-colors hover:border-spark hover:text-spark">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-[2rem] border border-ink/10 bg-white p-7 shadow-xl shadow-ink/5 sm:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">What We Deliver</p>
              <ul className="mt-6 space-y-4">
                {service.deliverables.map((d, i) => (
                  <motion.li
                    key={d}
                    initial={{ opacity: 0, x: -14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    className="flex items-start gap-3 text-sm font-medium text-ink sm:text-base"
                    data-testid={`deliverable-${i}`}
                  >
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-spark to-ember">
                      <Check className="h-3.5 w-3.5 text-night" strokeWidth={3} />
                    </span>
                    {d}
                  </motion.li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SPECIALISED SUB-SERVICES (crypto etc.) */}
      {service.subServices && (
        <section className="grain relative overflow-hidden bg-night py-20 lg:py-28" data-testid="sub-services-section">
          <div className="pointer-events-none absolute -left-32 top-0 h-80 w-80 rounded-full bg-spark/15 blur-[110px]" />
          <div className="pointer-events-none absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-ember/15 blur-[110px]" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-spark">Deep Capabilities</p>
              <h2 className="mt-3 max-w-2xl font-syne text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
                Everything crypto, <span className="bg-gradient-to-r from-spark to-ember bg-clip-text text-transparent">under one roof.</span>
              </h2>
            </Reveal>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {service.subServices.map((sub, i) => (
                <Reveal key={sub.name} delay={(i % 3) * 0.08}>
                  <div
                    className="group h-full rounded-3xl border border-white/10 bg-white/5 p-7 backdrop-blur transition-all duration-500 hover:-translate-y-2 hover:border-spark/40 hover:bg-white/10"
                    data-testid={`sub-service-${sub.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-spark to-ember text-night transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                      <sub.icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-syne text-lg font-bold text-white">{sub.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{sub.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <div className="mt-16 flex flex-wrap items-center justify-center gap-4 sm:gap-5" data-testid="chain-badges">
                <p className="w-full text-center font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
                  Chains We Build On
                </p>
                {CHAINS.map((c) => (
                  <span
                    key={c.name}
                    data-testid={`chain-badge-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-6 py-3 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-white/30 hover:bg-white/10"
                  >
                    <img src={c.logo} alt={`${c.name} logo`} className="h-6 w-6 transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                    <span className="font-syne text-sm font-bold text-white">{c.name}</span>
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* PREV / NEXT */}
      <section className="border-t border-ink/10 bg-white py-14">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <Link
            to={`/services/${prev.slug}`}
            data-testid="service-prev-link"
            className="group flex items-center gap-3 text-sm font-semibold text-slate-500 transition-colors hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            <span>
              <span className="block font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Previous</span>
              {prev.name}
            </span>
          </Link>
          <Link
            to={`/services/${next.slug}`}
            data-testid="service-next-link"
            className="group flex items-center gap-3 text-right text-sm font-semibold text-slate-500 transition-colors hover:text-ink sm:text-right"
          >
            <span>
              <span className="block font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Next</span>
              {next.name}
            </span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <CTASection serviceName={service.name} />
    </main>
  );
}
